import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { Item } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { useLocaleUtils } from 'common/hooks';
import { isDefined } from 'common/utils';

const useListItemInfo = (storageLocation: string | null) => {
  const intl = useIntl();
  const { getLocalizedUnit } = useLocaleUtils();

  const formatItemUnit = useCallback(
    (item: Item) => {
      if (item.display_unit === 'units' && item.number_of_units) {
        return `${item.number_of_units} ${intl.formatMessage({ id: 'menu-item.display-unit-pcs' })}`;
      }
      if ((item.display_unit === 'volume' || item.display_unit === 'volume_100ml') && item.volume_in_ml) {
        return item.volume_in_ml >= 1000
          ? `${item.volume_in_ml / 1000}${getLocalizedUnit('liter')}`
          : `${item.volume_in_ml}${getLocalizedUnit('milliliter')}`;
      }
      if ((item.display_unit === 'weight' || item.display_unit === 'weight_100g') && item.weight_in_grams) {
        return item.weight_in_grams >= 1000
          ? `${item.weight_in_grams / 1000}${getLocalizedUnit('kilogram')}`
          : `${item.weight_in_grams}${getLocalizedUnit('gram')}`;
      }
      if (item.sell_by_weight_config?.input_type === 'grams' && item.sell_by_weight_config.grams_per_step) {
        const weight =
          item.sell_by_weight_config.grams_per_step >= 1000
            ? `${item.sell_by_weight_config.grams_per_step / 1000}${getLocalizedUnit('kilogram')}`
            : `${item.sell_by_weight_config.grams_per_step}${getLocalizedUnit('gram')}`;
        return intl.formatMessage({ id: 'picking-route.item-weighed-step' }, { weight });
      }
      if (item.sell_by_weight_config?.input_type === 'number_of_items' && item.sell_by_weight_config.grams_per_step) {
        const weight =
          item.sell_by_weight_config.grams_per_step >= 1000
            ? `${item.sell_by_weight_config.grams_per_step / 1000}${getLocalizedUnit('kilogram')}`
            : `${item.sell_by_weight_config.grams_per_step}${getLocalizedUnit('gram')}`;
        return intl.formatMessage({ id: 'picking-route.item-weighed-piece' }, { weight });
      }
      return undefined;
    },
    [getLocalizedUnit, intl],
  );

  const formatItemInfo = useCallback(
    (item: Item) => {
      const info = [
        formatItemUnit(item) || undefined,
        item.barcode_gtin
          ? `${intl.formatMessage({ id: 'picking-route.item-gtin' })}: ${item.barcode_gtin}`
          : undefined,
        item.merchant_sku ? `${intl.formatMessage({ id: 'picking-route.item-sku' })}: ${item.merchant_sku}` : undefined,
        storageLocation
          ? `${intl.formatMessage({ id: 'picking-route.item-location' })}: ${storageLocation}`
          : undefined,
      ];
      return info.filter(isDefined).join(' • ');
    },
    [formatItemUnit, intl, storageLocation],
  );

  return { formatItemInfo };
};

export default useListItemInfo;
